import { CHANNELS_UNLIMITED } from '@disco/constants/FeatureTypes'
import type { GROUP_TYPE } from '@disco/constants/GroupTypes'
import {
  CUSTOM_CHANNEL,
  CUSTOM_INBOX,
  INBOX_TYPES,
  MAX_CHANNELS_LIMITED_MODE,
  PERSONAL_INBOX,
  PERSONAL_LIBRARY,
  SAVED_TO_DISCO,
  TEAM_INBOX,
  TEAM_LIBRARY,
  TERRITORY,
} from '@disco/constants/GroupTypes'
import {
  CHANNEL_CUSTOM_SORT,
  CHANNEL_DATE_SORT,
  CHANNEL_TITLE_SORT,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
} from '@disco/constants/SortTypes'
import {
  folderList,
  getChannelsBySearch,
  getFolderChannel,
  getGroupName,
  getGroupWithNesting,
  mapGroups,
  sortFolders,
} from '@disco/utils/groups'
import { indexDropdownOptions } from '@disco/utils/search'
import lodashMaxBy from 'lodash/maxBy'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import type {
  Group,
  GroupId,
  GroupLockSettings,
  GroupWithNesting,
  BusinessShare,
} from 'types/group'
import type { State } from 'types/redux'
import type { User } from 'types/user'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getChannelsSearchTerm } from '@disco/state-management/selectors/search'
import {
  getChannelPlaylistSortDirection,
  getChannelPlaylistSortParam,
  getChannelsCustomOrder,
  getChannelsSortDirection,
  getChannelsSortParam,
  getTerritoriesEnabled,
  getUser,
  getUserSettings,
} from '@disco/state-management/selectors/user'
/**
 * Gets all groups from the state.
 * @param state - The Redux state
 * @returns Array of group objects
 */
export const getGroups = (state: State): Group[] => state.groups.groups
/**
 * Checks if groups are currently loading.
 * @param state - The Redux state
 * @returns True if groups are loading, false otherwise
 */
export const getGroupsLoading = (state: State): boolean =>
  (state.groups.isFetching || !state.groups.isFetched) && !state.groups.error
/**
 * Gets groups with their nested folder structure resolved.
 * @param state - The Redux state
 * @returns Array of groups with folders property containing nested groups
 */
export const getGroupsWithNesting = createSelector(getGroups, groups => {
  const mappedGroups: Record<GroupId, GroupWithNesting> = {}
  groups.forEach(g => {
    mappedGroups[g.id] = { ...g, folders: [] }
  })
  groups.forEach(group => {
    const children = groups.filter(g => g.parent === group.id)
    // mappedGroups[group.id] was set in the previous forEach — always defined
    const mapped = mappedGroups[group.id]!
    mapped.folders = children.map(g => mappedGroups[g.id]!)
  })
  return groups.map(g => mappedGroups[g.id]!)
})
/**
 * Gets groups filtered by search term.
 * @param state - The Redux state
 * @returns Array of groups that match the search criteria
 */
export const getSearchFilterGroups = createSelector(
  getGroups,
  getChannelsSearchTerm,
  (groups, channelSearchTerm) => {
    if (!channelSearchTerm?.length) return groups
    return getChannelsBySearch(groups, channelSearchTerm) || []
  },
)
/**
 * Gets groups with nested structure and sorting applied.
 * @param state - The Redux state
 * @returns Array of groups with folders sorted according to user preferences
 */
export const getGroupsWithNestingAndSorting = createSelector(
  getGroups,
  getChannelsSortParam,
  getChannelsSortDirection,
  getChannelsCustomOrder,
  getChannelPlaylistSortParam,
  getChannelPlaylistSortDirection,
  (
    unsortedGroups,
    sortOrder,
    sortDirection,
    customOrder,
    folderSortParam,
    folderSortDirection,
  ) => {
    const channels = sortChannelSelectorFn(
      unsortedGroups,
      sortOrder,
      sortDirection,
      customOrder,
    ).filter(Boolean)
    const mappedGroups = mapGroups(unsortedGroups)
    unsortedGroups.forEach(group => {
      const children = unsortedGroups.filter(g => g.parent === group.id)
      // mappedGroups[group.id] was set in the previous forEach — always defined
      const mappedChannel = mappedGroups[group.id]!
      // `sortFolders` here mirrors `makeGetChannelFolders`
      mappedChannel.folders = sortFolders(
        children.map(g => mappedGroups[g.id]!),
        folderSortParam,
        folderSortDirection,
      )
    })
    return channels.map(g => mappedGroups[g.id])
  },
)
/**
 * Gets filtered groups with nested structure and sorting applied.
 * @param state - The Redux state
 * @returns Array of filtered groups with folders sorted according to user preferences
 */
export const getFilteredGroupsWithNestingAndSorting = createSelector(
  getGroups,
  getSearchFilterGroups,
  getChannelsSortParam,
  getChannelsSortDirection,
  getChannelsCustomOrder,
  getChannelPlaylistSortParam,
  getChannelPlaylistSortDirection,
  (
    unsortedGroups,
    unsortedSearchFilterGroups,
    sortOrder,
    sortDirection,
    customOrder,
    folderSortParam,
    folderSortDirection,
  ) => {
    if (!unsortedGroups || !unsortedSearchFilterGroups) return []
    const channels = sortChannelSelectorFn(
      unsortedSearchFilterGroups,
      sortOrder,
      sortDirection,
      customOrder,
    ).filter(Boolean)
    const mappedGroups: Record<GroupId, GroupWithNesting> = {}
    unsortedGroups.forEach(g => {
      mappedGroups[g.id] = { ...g, folders: [] }
    })
    unsortedSearchFilterGroups.forEach(channel => {
      const children = unsortedGroups.filter(g => g.parent === channel.id)
      // entries were populated in the forEach above, so each channel.id is present
      const mappedChannel = mappedGroups[channel.id]!

      const getNestedChildren = (groupId: number): GroupWithNesting[] => {
        const grandchildren = unsortedGroups.filter(g => g.parent === groupId)
        return grandchildren.map(child => ({
          ...mappedGroups[child.id]!,
          folders: getNestedChildren(child.id),
        })) as GroupWithNesting[]
      }

      const nestedChildren = children.map(g => {
        // g is from unsortedGroups, which was mapped into mappedGroups above
        const channel = { ...mappedGroups[g.id]! } as GroupWithNesting
        return getGroupWithNesting(channel)
      })
      mappedChannel.folders = sortFolders(
        nestedChildren.map(child => ({
          ...child,
          folders: getNestedChildren(child.id),
        })) as GroupWithNesting[],
        folderSortParam,
        folderSortDirection,
      )
    })
    return channels.map(g => mappedGroups[g.id]!)
  },
)
/**
 * Gets the total number of groups.
 * @param state - The Redux state
 * @returns The count of groups
 */
export const getGroupsCount = (state: State): number =>
  state.groups.groups.length
/**
 * Map of group id → group, derived from `getGroups`.
 */
export const getGroupsById = createSelector(
  getGroups,
  groups => new Map<GroupId, Group>(groups.map(g => [g.id, g])),
)
/**
 * Gets a specific group by ID.
 * @param state - The Redux state
 * @param props - Object containing the group ID
 * @param props.groupId - The group ID to retrieve
 * @returns The group object, or null/undefined if not found
 */
export const getGroup = (
  state: State,
  {
    groupId,
  }: {
    groupId: number
  },
): Group | null | undefined => getGroupsById(state).get(groupId)

/**
 * Gets a specific group with its nested structure.
 * @param state - The Redux state
 * @param props - Object containing the group ID
 * @param props.groupId - The group ID to retrieve
 * @returns The group with nesting structure, or null/undefined if not found
 */
const getNestedGroupsById = createSelector(
  getGroupsWithNesting,
  groups =>
    new Map<GroupId, GroupWithNesting>(
      groups.map(g => [g.id, g] as [GroupId, GroupWithNesting]),
    ),
)

export const getNestedGroup = (
  state: State,
  {
    groupId,
  }: {
    groupId: number
  },
): GroupWithNesting | null | undefined =>
  getNestedGroupsById(state).get(groupId)

/**
 * Factory function that creates a selector to get a specific group.
 * @returns A selector function for getting a group by ID
 */
export const makeGetGroup = (): typeof getGroup => getGroup
/**
 * Checks if a group is a subfolder (has a parent that also has a parent).
 * @param state - The Redux state
 * @param props - Object containing the group ID
 * @param props.groupId - The group ID to check
 * @returns True if the group is a subfolder, false otherwise
 */
export const getIsSubfolder = (
  state: State,
  {
    groupId,
  }: {
    groupId: number
  },
): boolean => {
  const group = getGroup(state, {
    groupId,
  })
  const parentGroup =
    group && group.parent
      ? getGroup(state, {
          groupId: group.parent,
        })
      : null
  return !!parentGroup?.parent
}
/**
 * Gets the type of a specific group.
 * @param state - The Redux state
 * @param props - Object containing the group ID
 * @param props.groupId - The group ID to get type for
 * @returns The group type, or null if group not found
 */
export const getGroupType = (
  state: State,
  {
    groupId,
  }: {
    groupId: number
  },
): null | GROUP_TYPE => {
  const group = getGroup(state, {
    groupId,
  })
  if (!group) return null
  return group.group_type
}
/**
 * Gets groups associated with the current user.
 * @param state - The Redux state
 * @returns Array of user's groups
 */
export const getUserGroups = (state: State): Group[] => state.user.groups
/**
 * Gets a group by its slug.
 * @param state - The Redux state
 * @param props - Object containing the slug
 * @param props.slug - The group slug to search for
 * @returns The group with matching slug, or null/undefined if not found
 */
export const getGroupBySlug = (
  state: State,
  {
    slug,
  }: {
    slug: string
  },
): Group | null | undefined =>
  getGroups(state).find(g => !g.parent && g.slug === slug)
/**
 * Gets the user's personal library group.
 * @param state - The Redux state
 * @returns The personal library group, or undefined if not found
 */
export const getPersonalLibrary = createSelector(getUserGroups, groups =>
  groups.find(g => g.group_type === PERSONAL_LIBRARY),
)
/**
 * Gets the user's team library group.
 * @param state - The Redux state
 * @returns The team library group, or undefined if not found
 */
export const getTeamLibrary = createSelector(getUserGroups, groups =>
  groups.find(g => g.group_type === TEAM_LIBRARY),
)
/**
 * Gets the user's personal inbox group.
 * @param state - The Redux state
 * @returns The personal inbox group, or undefined if not found
 */
export const getPersonalInbox = createSelector(getUserGroups, groups =>
  groups.find(g => g.group_type === PERSONAL_INBOX),
)
/**
 * Gets the user's team inbox group.
 * @param state - The Redux state
 * @returns The team inbox group, or undefined if not found
 */
export const getTeamInbox = createSelector(getUserGroups, groups =>
  groups.find(g => g.group_type === TEAM_INBOX),
)
/**
 * Gets all custom inbox groups.
 * @param state - The Redux state
 * @returns Array of custom inbox groups
 */
export const getCustomInboxes = createSelector(getGroups, groups =>
  groups.filter(g => g.group_type === CUSTOM_INBOX),
)
/**
 * Gets all inbox type groups.
 * @param state - The Redux state
 * @returns Array of inbox groups (personal, team, and custom)
 */
export const getInboxes = createSelector(getGroups, groups =>
  groups.filter(g => INBOX_TYPES.includes(g.group_type)),
)
/**
 * Gets all custom channel groups (top-level only, no subfolders).
 * @param state - The Redux state
 * @returns Array of custom channel groups
 */
export const getCustomChannels = createSelector(getGroups, groups =>
  groups.filter(g => g.group_type === CUSTOM_CHANNEL && !g.parent),
)
/**
 * Gets custom channels available in settings (excludes auto-tagged channels).
 * @param state - The Redux state
 * @returns Array of settable custom channel groups
 */
export const getSettingsChannels = createSelector(getGroups, groups =>
  groups.filter(
    g => g.group_type === CUSTOM_CHANNEL && !g.parent && !g.is_auto_tagged,
  ),
)
/**
 * Gets custom channels with their nested folder structure.
 * @param state - The Redux state
 * @returns Array of custom channel groups with folders
 */
export const getCustomChannelsWithNesting = createSelector(
  getGroupsWithNesting,
  groups => groups.filter(g => g.group_type === CUSTOM_CHANNEL && !g.parent),
)

/**
 * Internal function to sort channels by different criteria.
 * @param channels - Array of channel groups
 * @param sortParam - The sort parameter (date, title, etc.)
 * @param sortDirection - The sort direction (ASC/DESC)
 * @returns Sorted array of channels
 */
const _getSortedChannels = (channels, sortParam, sortDirection) => {
  const lessThan = sortDirection === SORT_DIRECTION_ASC ? -1 : 1
  const moreThan = sortDirection === SORT_DIRECTION_ASC ? 1 : -1

  switch (sortParam) {
    case CHANNEL_DATE_SORT:
      return channels.slice().sort((a, b) => {
        const aCreated = new Date(a.created).getTime()
        const bCreated = new Date(b.created).getTime()
        return (aCreated - bCreated || a.id - b.id) * moreThan
      })

    case CHANNEL_TITLE_SORT:
      return channels.slice().sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) return lessThan
        if (a.name.toLowerCase() > b.name.toLowerCase()) return moreThan
        return 0
      })

    default:
      return channels
  }
}

const _applySortToChannels = (
  channels: Group[],
  sortOrder: string | null | undefined,
  sortDirection: string | null | undefined,
  customOrder: number[] | null | undefined,
) => {
  if (!channels.length) return []

  if (sortOrder === CHANNEL_CUSTOM_SORT) {
    // custom order hasn't been set yet, return channels sorted by default
    if (!customOrder) {
      return _getSortedChannels(channels, CHANNEL_DATE_SORT, SORT_DIRECTION_ASC)
    }

    if (Array.isArray(customOrder)) {
      // channels saved in settings
      const orderedChannels = customOrder
        .map(id => channels.find(channel => channel.id === id))
        .filter((channel): channel is Group => Boolean(channel))
      // channels not present in saved custom order settings
      const additionalChannels = channels.filter(
        channel => customOrder.indexOf(channel.id) === -1,
      )
      return [...orderedChannels, ...additionalChannels]
    }
  }

  return _getSortedChannels(
    channels,
    sortOrder || CHANNEL_DATE_SORT,
    sortDirection || SORT_DIRECTION_ASC,
  )
}

/**
 * Sorts channels based on user preferences including custom ordering.
 * @param groups - Array of all groups
 * @param sortOrder - The sort order preference
 * @param sortDirection - The sort direction preference
 * @param customOrder - Array of custom ordered channel IDs
 * @returns Sorted array of channel groups
 */
const sortChannelSelectorFn = createSelector(
  [
    groups =>
      groups?.filter(g => g.group_type === CUSTOM_CHANNEL && !g.parent) || [],
    (_groups, sortOrder) => sortOrder,
    (_groups, _sortOrder, sortDirection) => sortDirection,
    (_groups, _sortOrder, _sortDirection, customOrder) => customOrder,
  ],
  _applySortToChannels,
)

const getCustomTopLevelGroups = createSelector(
  [getGroups],
  (groups): Group[] =>
    groups?.filter(g => g.group_type === CUSTOM_CHANNEL && !g.parent) ?? [],
)

/**
 * Gets user's channels sorted according to their preferences.
 * @param state - The Redux state
 * @returns Array of sorted channel groups
 */
export const getUserSortedChannels = createSelector(
  [
    getCustomTopLevelGroups,
    getChannelsSortParam,
    getChannelsSortDirection,
    getChannelsCustomOrder,
  ],
  _applySortToChannels,
)
/**
 * Factory function that creates a selector to get folders for a specific channel.
 * @returns A selector function for getting channel folders
 */
export const makeGetChannelFolders = (): ((
  state: State,
  props: any,
) => Group[]) =>
  createSelector(
    [
      getGroups,
      getGroup,
      getChannelPlaylistSortParam,
      getChannelPlaylistSortDirection,
    ],
    (groups, channel, sortParam, sortDirection) => {
      // also implemented in `getGroupsWithNestingAndSorting`
      const folders = groups.filter(folder => folder.parent === channel?.id)
      return sortFolders(folders, sortParam, sortDirection)
    },
  )
/**
 * Gets base groups available for searching (libraries and inboxes).
 * @param state - The Redux state
 * @returns Array of base search groups
 */
export const getBaseSearchGroups = createSelector(
  [getTeamLibrary, getPersonalLibrary, getPersonalInbox, getTeamInbox],
  (teamLib, personalLib, personalInbox, teamInbox) => {
    return [teamLib, personalLib, personalInbox, teamInbox].filter(Boolean)
  },
)
/**
 * Gets all groups available for searching including custom channels and folders.
 * @param state - The Redux state
 * @returns Array of searchable groups
 */
export const getSearchGroups = createSelector(
  [
    getPersonalLibrary,
    getPersonalInbox,
    getTeamInbox,
    getCustomInboxes,
    getCustomChannelsWithNesting,
  ],
  (personalLib, personalInbox, teamInbox, customInboxes, customChannels) => {
    return [
      personalLib,
      personalInbox,
      teamInbox,
      ...customInboxes, // folderList second argument is not optional
      ...folderList(customChannels, () => true),
    ].filter((g): g is Group | GroupWithNesting => Boolean(g))
  },
)
/**
 * Gets search groups formatted and indexed for dropdown options.
 * @param state - The Redux state
 * @returns Array of indexed dropdown options for search groups
 */
export const getSearchGroupsIndexed = createSelector(
  [getSearchGroups],
  groups =>
    indexDropdownOptions(
      groups.map(group => ({
        value: group.id,
        label: getGroupName(group),
      })),
    ),
)
/**
 * Gets territory groups if territories feature is enabled.
 * @param state - The Redux state
 * @returns Array of territory groups, or empty array if territories disabled
 */
export const getTerritories = createSelector(
  [getTerritoriesEnabled, getGroups],
  (territoriesEnabled, groups: Group[]) =>
    territoriesEnabled ? groups.filter(g => g.group_type === TERRITORY) : [],
)
/**
 * Gets territories accessible to the current user.
 * @param state - The Redux state
 * @returns Array of user's territory groups
 * @note Selector placed here to avoid circular dependencies
 */
export const getUserTerritories = createSelector(
  [getTerritoriesEnabled, getUserGroups, getTerritories],
  (territoriesEnabled, userGroups: Group[], territories: Group[]) =>
    territoriesEnabled
      ? territories.filter(t => userGroups.some(g => g.id === t.id))
      : [],
)
/**
 * Gets all library groups accessible to the user including territories and inboxes.
 * @param state - The Redux state
 * @returns Array of user's library groups
 */
export const getUserLibraries = createSelector(
  [getUser, getGroups, getUserTerritories],
  (user: User, groups: Group[], userTerritories: Group[]) => {
    return [
      ...(!user.business?.private_metadata?.hide_territory_groups_in_navigation
        ? userTerritories
        : []),
      ...user.libraries.slice(0, user.libraries.length - 1),
      ...groups.filter(group => group.group_type === TEAM_INBOX),
      ...groups.filter(group => group.group_type === CUSTOM_INBOX),
      ...groups.filter(group => group.group_type === SAVED_TO_DISCO),
      ...user.libraries.slice(user.libraries.length - 1),
    ]
  },
)
/**
 * Gets inbox groups from the user's libraries.
 * @param state - The Redux state
 * @returns Array of user's inbox groups
 */
export const getUserInboxes = createSelector(
  getUserLibraries,
  (groups: Group[]) =>
    groups.filter((group: Group) => INBOX_TYPES.includes(group.group_type)),
)

/**
 * Gets lock settings for a group from its parent channel.
 * @param groups - Array of all groups
 * @param group - The group to get locks for
 * @returns Lock settings object or null if no locks
 */
const getLocks = (groups, group): GroupLockSettings | null | undefined => {
  const channel = getFolderChannel(groups, group)

  if (channel) {
    return {
      lock_content: channel.lock_content ?? false,
      lock_structure: channel.lock_structure ?? false,
    }
  }

  return null
}

/**
 * Gets lock settings for a specific group.
 * @param state - The Redux state
 * @param props - Object containing group ID
 * @returns Lock settings for the group
 */
const getGroupLocks = createSelector([getGroups, getGroup], (groups, group) => {
  return group ? getLocks(groups, group) : null
})
/**
 * Factory function that creates a selector to get group locks.
 * @returns A selector function for getting group lock settings
 */
export const makeGetGroupLocks = (): typeof getGroupLocks => getGroupLocks
/**
 * Hook to get lock settings for a specific group.
 * @param groupId - The group ID to get locks for
 * @returns Lock settings for the group
 */
export const useGroupLocks = (
  groupId: GroupId | null | undefined,
): GroupLockSettings | null | undefined => {
  const selector = useMemo(makeGetGroupLocks, [])
  return useSelector((state: State) =>
    groupId
      ? selector(state, {
          groupId,
        })
      : null,
  )
}

/**
 * Extracts group IDs from props.
 * @param _ - Unused state parameter
 * @param props - Object containing group IDs
 * @param props.ids - Array of group IDs
 * @returns Array of group IDs
 */
const getGroupIds = (
  _: any,
  props: {
    ids: GroupId[]
  },
): GroupId[] => props.ids

/**
 * Gets restrictions (lock settings) for multiple groups.
 * @param state - The Redux state
 * @param props - Object containing group IDs
 * @returns Object mapping group IDs to their lock settings
 */
const getGroupsRestrictions = createSelector(
  [getGroups, getGroupIds],
  (groups, ids) => {
    const targetGroups = ids.map(id => groups.find(g => g.id === id))
    return targetGroups.reduce((acc, cur) => {
      if (cur) {
        return { ...acc, [cur.id]: getLocks(groups, cur) }
      } else {
        return acc
      }
    }, {})
  },
)
/**
 * Factory function that creates a selector to get multiple group restrictions.
 * @returns A selector function for getting group restrictions
 */
export const makeGetGroupsRestrictions = (): typeof getGroupsRestrictions =>
  getGroupsRestrictions
/**
 * Gets the maximum number of channels allowed in limited mode.
 * @param state - The Redux state
 * @returns Maximum channel count including auto-tagged channels
 */
export const getMaxChannelsLimitedMode = createSelector(
  [getGroups],
  (groups: Group[]) => {
    return (
      MAX_CHANNELS_LIMITED_MODE + groups.filter(l => l.is_auto_tagged).length
    )
  },
)
/**
 * Gets the timestamp of the most recently modified group.
 * @param state - The Redux state
 * @returns ISO timestamp of last group update, or null if no groups
 */
export const getGroupsLastUpdate = createSelector(getGroups, groups => {
  const modifiedString = lodashMaxBy<Group>(
    groups,
    g => new Date(g.modified),
  )?.modified
  if (!modifiedString) return null
  const modified = new Date(modifiedString)
  // increase timestamp so backend won't include last group in results
  modified.setMilliseconds(modified.getMilliseconds() + 1)
  return modified.toISOString()
})
/**
 * Gets groups that have content locked (supply channels).
 * @param state - The Redux state
 * @returns Array of groups with locked content
 */
export const getSupplyChannelGroups = createSelector(
  [getGroups],
  (groups: Group[]) => {
    return groups.filter(
      g => g.lock_content || getLocks(groups, g)?.lock_content,
    )
  },
)
/**
 * Checks if the number of custom channels exceeds the limit for limited mode.
 * @param state - The Redux state
 * @returns True if channel count exceeds limit, false otherwise
 */
export const isGroupsExcessLimit = createSelector(
  [
    getCustomChannels,
    state =>
      isFeatureEnabled(state, {
        featureName: CHANNELS_UNLIMITED,
      }),
    getMaxChannelsLimitedMode,
  ],
  (customChannels: Group[], isFeatureEnabled, maxChannelsLimitedMode) => {
    const channelsLimited = !isFeatureEnabled
    return channelsLimited && customChannels.length >= maxChannelsLimitedMode
  },
)
/**
 * Gets the user's default library based on their settings.
 * @param state - The Redux state
 * @returns The default library group
 */
export const getDefaultLibrary = createSelector(
  [getUserLibraries, getUserSettings],
  (groups, settings) => {
    const group = settings?.explorer_default_view ?? TEAM_LIBRARY
    return groups.find(lib => lib.group_type === group)
  },
)
/**
 * Gets the auto-tagging group if it exists.
 * @param state - The Redux state
 * @returns The auto-tagging group, or undefined if not found
 */
export const getAutoTaggingGroup = createSelector([getGroups], groups =>
  groups.find(g => g.is_auto_tagged),
)
/**
 * Gets the 3 most recently created channels.
 * @param state - The Redux state
 * @returns Array of the 3 most recent channel groups
 */
export const getRecentChannels = createSelector(getGroups, groups => {
  const channels = groups.filter(
    g => g.group_type === CUSTOM_CHANNEL && !g.parent,
  )
  return _getSortedChannels(
    channels,
    CHANNEL_DATE_SORT,
    SORT_DIRECTION_DESC,
  ).slice(0, 3)
})
/**
 * Gets business invites/shares for a specific group.
 * @param state - The Redux state
 * @param props - Object containing the group ID
 * @param props.groupId - The group ID to get business invites for
 * @returns Array of business share objects
 */
export const getGroupBusinessInvites = (
  state: State,
  {
    groupId,
  }: {
    groupId: number
  },
): BusinessShare[] =>
  getGroup(state, {
    groupId,
  })?.shared_with_businesses ?? []

export const getGroupsFromExternalShare = createSelector([getGroups], groups =>
  groups.filter(group => group.business_role === 'partner'),
)
